/**
 * NotificationBell Component
 * Displays unread admin message notifications in a dropdown
 * Clicking a notification opens the chat bubble
 */

import { useState, useEffect, useRef } from 'react'
import { BellIcon, X } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { useChat } from '../contexts/ChatContext'

export function NotificationBell() {
  const [unreadMessages, setUnreadMessages] = useState([])
  const [unreadBookingMessages, setUnreadBookingMessages] = useState([])
  const [cancellationNotifications, setCancellationNotifications] = useState([])
  const [emergencyAlerts, setEmergencyAlerts] = useState([])
  const [isOpen, setIsOpen] = useState(false)
  const { user, photographerProfile } = useAuth()
  const { openAdminChat, openChat } = useChat()
  const dropdownRef = useRef(null)

  // Fetch unread messages
  useEffect(() => {
    if (!user) return

    const fetchUnreadMessages = async () => {
      try {
        const { data, error } = await supabase
          .from('admin_messages')
          .select(`
            id,
            message_text,
            created_at,
            sender:users!sender_id(full_name)
          `)
          .eq('user_id', user.id)
          .eq('is_read_user', false)
          .is('deleted_at', null)
          .order('created_at', { ascending: false })
          .limit(10)

        if (!error) {
          setUnreadMessages(data || [])
        }
      } catch (err) {
        console.error('Error fetching unread messages:', err)
      }
    }

    fetchUnreadMessages()

    // Subscribe to realtime updates
    const channel = supabase
      .channel('unread-messages-notifications')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'admin_messages',
          filter: `user_id=eq.${user.id}`
        },
        () => {
          // Refetch messages when changes occur
          fetchUnreadMessages()
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [user])

  // Fetch unread booking messages (for both customers and photographers)
  useEffect(() => {
    if (!user) return

    const isPhotographer = !!photographerProfile?.id
    console.log('🔔 NotificationBell: Setting up for', isPhotographer ? 'photographer' : 'customer')

    const fetchUnreadBookingMessages = async () => {
      console.log('🔔 NotificationBell: Fetching unread booking messages...')
      try {
        let query = supabase
          .from('booking_chat_threads')
          .select(`
            id,
            booking_id,
            last_message_at,
            unread_count_customer,
            unread_count_talent,
            customer:users!booking_chat_threads_customer_id_fkey(full_name),
            talent:users!booking_chat_threads_talent_user_id_fkey(full_name),
            booking:bookings!booking_chat_threads_booking_id_fkey(
              event_date,
              event_type
            )
          `)
          .order('last_message_at', { ascending: false })
          .limit(10)

        // Filter based on user role
        if (isPhotographer) {
          query = query
            .eq('talent_user_id', user.id)
            .gt('unread_count_talent', 0)
        } else {
          query = query
            .eq('customer_id', user.id)
            .gt('unread_count_customer', 0)
        }

        const { data: threads, error } = await query
        console.log('🔔 NotificationBell: Query result', { threadCount: threads?.length, error })

        if (!error && threads) {
          const bookingMessages = await Promise.all(
            threads.map(async (thread) => {
              const { data: messages } = await supabase
                .from('booking_messages')
                .select(`
                  id,
                  thread_id,
                  message_text,
                  created_at,
                  sender:users!sender_id(full_name)
                `)
                .eq('thread_id', thread.id)
                .neq('sender_id', user.id)
                .order('created_at', { ascending: false })
                .limit(1)

              const latestMessage = messages?.[0]
              if (latestMessage) {
                return {
                  ...latestMessage,
                  booking_id: thread.booking_id,
                  thread_id: thread.id,
                  event_date: thread.booking?.event_date,
                  event_type: thread.booking?.event_type,
                  unread_count: isPhotographer
                    ? thread.unread_count_talent
                    : thread.unread_count_customer,
                  sender_display_name: isPhotographer
                    ? (thread.customer?.full_name || 'Your Client')
                    : (thread.talent?.full_name || latestMessage.sender?.full_name || 'Your Wedding Team'),
                  is_from_client: isPhotographer
                }
              }
              return null
            })
          )

          setUnreadBookingMessages(bookingMessages.filter(Boolean))
        }
      } catch (err) {
        console.error('Error fetching unread booking messages:', err)
      }
    }

    fetchUnreadBookingMessages()

    // Subscribe to the current user's conversation counters.
    const channelName = isPhotographer
      ? `booking-unread-photographer-${photographerProfile.id}`
      : `booking-unread-customer-${user.id}`

    console.log('🔔 NotificationBell: Subscribing to channel', channelName)

    const bookingChannel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'booking_chat_threads',
          filter: isPhotographer
            ? `talent_user_id=eq.${user.id}`
            : `customer_id=eq.${user.id}`
        },
        () => {
          fetchUnreadBookingMessages()
        }
      )
      .subscribe((status) => {
        console.log('🔔 NotificationBell: Subscription status', status)
      })

    // Also listen for manual refetch events (triggered when chat messages are read)
    const handleChatMessagesRead = () => {
      console.log('🔔 NotificationBell: Received chat-messages-read event, refetching...')
      fetchUnreadBookingMessages()
    }
    window.addEventListener('chat-messages-read', handleChatMessagesRead)

    return () => {
      supabase.removeChannel(bookingChannel)
      window.removeEventListener('chat-messages-read', handleChatMessagesRead)
    }
  }, [user, photographerProfile])

  // Fetch and subscribe to talent cancellation notifications
  useEffect(() => {
    if (!user) return

    const fetchCancellationNotifications = async () => {
      try {
        const { data, error } = await supabase
          .from('talent_cancellation_notifications')
          .select(`
            id,
            booking_id,
            notification_type,
            days_before_event,
            created_at,
            replacement_selected,
            status,
            booking:bookings(
              id,
              event_date,
              event_type
            )
          `)
          .eq('customer_id', user.id)
          .eq('status', 'pending')
          .eq('replacement_selected', false)
          .order('created_at', { ascending: false })
          .limit(10)

        if (!error) {
          setCancellationNotifications(data || [])
        }
      } catch (err) {
        console.error('Error fetching cancellation notifications:', err)
      }
    }

    fetchCancellationNotifications()

    // Subscribe to realtime updates for cancellation notifications
    const cancellationChannel = supabase
      .channel('talent-cancellation-notifications')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'talent_cancellation_notifications',
          filter: `customer_id=eq.${user.id}`
        },
        () => {
          // Refetch notifications when changes occur
          fetchCancellationNotifications()
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(cancellationChannel)
    }
  }, [user])

  // Fetch and subscribe to emergency talent alerts (for photographers only)
  useEffect(() => {
    if (!user || !photographerProfile) return

    const fetchEmergencyAlerts = async () => {
      try {
        const { data, error } = await supabase
          .from('emergency_talent_alerts')
          .select(`
            id,
            booking_id,
            claimed,
            status,
            alert_sent_at,
            booking:bookings(
              id,
              event_date,
              event_type,
              location_city,
              location_state,
              venue_address
            )
          `)
          .eq('talent_id', photographerProfile.id)
          .in('status', ['sent', 'viewed'])
          .eq('claimed', false)
          .order('alert_sent_at', { ascending: false })
          .limit(10)

        if (!error) {
          setEmergencyAlerts(data || [])
        }
      } catch (err) {
        console.error('Error fetching emergency alerts:', err)
      }
    }

    fetchEmergencyAlerts()

    // Subscribe to realtime updates for emergency alerts
    const emergencyChannel = supabase
      .channel('emergency-talent-alerts')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'emergency_talent_alerts',
          filter: `talent_id=eq.${photographerProfile.id}`
        },
        () => {
          // Refetch alerts when changes occur
          fetchEmergencyAlerts()
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(emergencyChannel)
    }
  }, [user, photographerProfile])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const handleNotificationClick = () => {
    setIsOpen(false)
    openAdminChat()
  }

  const handleBookingMessageClick = (bookingId, threadId = null) => {
    setIsOpen(false)
    openChat(bookingId, threadId)
  }

  const handleCancellationClick = async (notification) => {
    setIsOpen(false)
    // The Love & Photos team assigns the replacement (policy 3 Sep 2026); customers no longer browse for one.
    window.location.href = '/dashboard'

    // Mark notification as acknowledged
    try {
      await supabase
        .from('talent_cancellation_notifications')
        .update({ status: 'acknowledged' })
        .eq('id', notification.id)
    } catch (err) {
      console.error('Error acknowledging cancellation notification:', err)
    }
  }

  const handleEmergencyAlertClick = async (alert) => {
    setIsOpen(false)

    // Navigate to emergency job details page (will be created in later task)
    window.location.href = `/talent/emergency-jobs/${alert.booking_id}`

    // Mark alert as viewed
    try {
      await supabase
        .from('emergency_talent_alerts')
        .update({ status: 'viewed' })
        .eq('id', alert.id)
    } catch (err) {
      console.error('Error marking emergency alert as viewed:', err)
    }
  }

  const handleRemoveNotification = async (messageId, e) => {
    e.stopPropagation()

    // Mark as read
    try {
      await supabase
        .from('admin_messages')
        .update({ is_read_user: true, read_at_user: new Date().toISOString() })
        .eq('id', messageId)

      // Remove from local state
      setUnreadMessages(prev => prev.filter(msg => msg.id !== messageId))
    } catch (err) {
      console.error('Error marking message as read:', err)
    }
  }

  const handleRemoveCancellationNotification = async (notificationId, e) => {
    e.stopPropagation()

    // Mark as acknowledged
    try {
      await supabase
        .from('talent_cancellation_notifications')
        .update({ status: 'acknowledged' })
        .eq('id', notificationId)

      // Remove from local state
      setCancellationNotifications(prev => prev.filter(notif => notif.id !== notificationId))
    } catch (err) {
      console.error('Error dismissing cancellation notification:', err)
    }
  }

  const handleRemoveEmergencyAlert = async (alertId, e) => {
    e.stopPropagation()

    // Mark as expired
    try {
      await supabase
        .from('emergency_talent_alerts')
        .update({ status: 'expired' })
        .eq('id', alertId)

      // Remove from local state
      setEmergencyAlerts(prev => prev.filter(alert => alert.id !== alertId))
    } catch (err) {
      console.error('Error dismissing emergency alert:', err)
    }
  }

  const markAllAsRead = async (e) => {
    e.stopPropagation()

    try {
      // Mark all messages as read
      const messageIds = unreadMessages.map(msg => msg.id)
      if (messageIds.length > 0) {
        await supabase
          .from('admin_messages')
          .update({ is_read_user: true, read_at_user: new Date().toISOString() })
          .in('id', messageIds)
      }

      // Mark all cancellation notifications as acknowledged
      const cancellationIds = cancellationNotifications.map(notif => notif.id)
      if (cancellationIds.length > 0) {
        await supabase
          .from('talent_cancellation_notifications')
          .update({ status: 'acknowledged' })
          .in('id', cancellationIds)
      }

      // Mark all emergency alerts as expired
      const emergencyIds = emergencyAlerts.map(alert => alert.id)
      if (emergencyIds.length > 0) {
        await supabase
          .from('emergency_talent_alerts')
          .update({ status: 'expired' })
          .in('id', emergencyIds)
      }

      setUnreadMessages([])
      setCancellationNotifications([])
      setEmergencyAlerts([])
    } catch (err) {
      console.error('Error marking all as read:', err)
    }
  }

  const formatTime = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffTime = Math.abs(now - date)
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))

    if (diffDays === 0) {
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    } else if (diffDays === 1) {
      return 'Yesterday'
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    }
  }

  const unreadCount = unreadMessages.length + unreadBookingMessages.length + cancellationNotifications.length + emergencyAlerts.length

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-foreground hover:text-foreground/80 transition"
        aria-label={unreadCount > 0 ? `${unreadCount} unread message${unreadCount !== 1 ? 's' : ''}` : 'Messages'}
      >
        <BellIcon className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-2xl border border-gray-200 z-50 max-h-[400px] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b border-gray-200">
            <h4 className="font-semibold text-gray-900">Notifications</h4>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-xs text-primary-600 hover:text-primary-700 font-medium"
              >
                Mark all read
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className="overflow-y-auto flex-1">
            {unreadCount === 0 ? (
              <p className="text-sm text-gray-500 text-center py-8">
                No new notifications
              </p>
            ) : (
              <div className="divide-y divide-gray-100">
                {/* Cancellation Notifications (for customers - shown first) */}
                {cancellationNotifications.map((notification) => (
                  <div
                    key={`cancel-${notification.id}`}
                    onClick={() => handleCancellationClick(notification)}
                    className="p-3 hover:bg-orange-50 cursor-pointer transition-colors relative group bg-orange-25"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-orange-900 mb-1">
                          {notification.notification_type === 'emergency' ? '⚠️ Urgent: ' : ''}
                          Photographer Cancellation
                        </p>
                        <p className="text-sm text-gray-700 mb-1">
                          Your photographer for {notification.booking?.event_type || 'your event'} on{' '}
                          {(() => {
                            // Parse date without timezone conversion (date-only format)
                            const dateStr = notification.booking?.event_date
                            if (!dateStr) return 'your event date'
                            const [year, month, day] = dateStr.split('T')[0].split('-')
                            const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
                            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                          })()}{' '}
                          has cancelled ({notification.days_before_event} days before event).
                        </p>
                        <p className="text-sm font-medium text-primary-600">
                          Our team is assigning your replacement and will confirm shortly
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {formatTime(notification.created_at)}
                        </p>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-orange-500 rounded-full mt-1 flex-shrink-0" />
                        <button
                          onClick={(e) => handleRemoveCancellationNotification(notification.id, e)}
                          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-orange-200 rounded"
                        >
                          <X className="w-3 h-3 text-gray-600" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Emergency Job Alerts (for photographers) */}
                {emergencyAlerts.map((alert) => (
                  <div
                    key={`emergency-${alert.id}`}
                    onClick={() => handleEmergencyAlertClick(alert)}
                    className="p-3 hover:bg-red-50 cursor-pointer transition-colors relative group bg-red-25"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-red-900 mb-1">
                          🚨 Emergency Gig Available
                        </p>
                        <p className="text-sm text-gray-700 mb-1">
                          {alert.booking?.event_type || 'Event'} on{' '}
                          {(() => {
                            // Parse date without timezone conversion (date-only format)
                            const dateStr = alert.booking?.event_date
                            if (!dateStr) return 'event date'
                            const [year, month, day] = dateStr.split('T')[0].split('-')
                            const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
                            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                          })()}
                        </p>
                        <p className="text-xs text-gray-600 mb-1">
                          📍 {alert.booking?.location_city && alert.booking?.location_state
                            ? `${alert.booking.location_city}, ${alert.booking.location_state}`
                            : alert.booking?.venue_address?.city && alert.booking?.venue_address?.state
                            ? `${alert.booking.venue_address.city}, ${alert.booking.venue_address.state}`
                            : 'Location available'}
                        </p>
                        <p className="text-sm font-medium text-primary-600">
                          Click to view details and claim
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {formatTime(alert.alert_sent_at)}
                        </p>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-red-500 rounded-full mt-1 flex-shrink-0" />
                        <button
                          onClick={(e) => handleRemoveEmergencyAlert(alert.id, e)}
                          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-red-200 rounded"
                        >
                          <X className="w-3 h-3 text-gray-600" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Booking Chat Notifications (for both customers and photographers) */}
                {unreadBookingMessages.map((message) => (
                  <div
                    key={`booking-msg-${message.id}`}
                    onClick={() => handleBookingMessageClick(message.booking_id, message.thread_id)}
                    className="p-3 hover:bg-blue-50 cursor-pointer transition-colors relative group"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-blue-900 mb-1">
                          💬 New message from {message.sender_display_name}
                        </p>
                        <p className="text-sm text-gray-600 line-clamp-2 mb-1">
                          {message.message_text}
                        </p>
                        <p className="text-xs text-gray-500">
                          {message.event_type} • {(() => {
                            const dateStr = message.event_date
                            if (!dateStr) return ''
                            const [year, month, day] = dateStr.split('T')[0].split('-')
                            const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
                            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                          })()}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {formatTime(message.created_at)}
                        </p>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-1 flex-shrink-0" />
                      </div>
                    </div>
                  </div>
                ))}

                {/* Admin Message Notifications */}
                {unreadMessages.map((message) => (
                  <div
                    key={`msg-${message.id}`}
                    onClick={handleNotificationClick}
                    className="p-3 hover:bg-gray-50 cursor-pointer transition-colors relative group"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 mb-1">
                          {message.sender?.full_name || 'Support Team'}
                        </p>
                        <p className="text-sm text-gray-600 line-clamp-2 mb-1">
                          {message.message_text}
                        </p>
                        <p className="text-xs text-gray-400">
                          {formatTime(message.created_at)}
                        </p>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-primary rounded-full mt-1 flex-shrink-0" />
                        <button
                          onClick={(e) => handleRemoveNotification(message.id, e)}
                          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-200 rounded"
                        >
                          <X className="w-3 h-3 text-gray-600" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default NotificationBell
